// client/src/components/creditAssignment/CreditAssignmentRequestDetail.js
//
// Dettaglio richiesta di cessione del credito.
// - admin: inserisce Anticipo / Costo gestione pratica / Costo veicolo
//   (Saldo e Costo totale calcolati). Salvando -> stato pending_approval.
// - azienda: vede le condizioni e dà l'approvazione finale
//   ("Conferma operazione") -> stato completed.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  formatCurrencyWithSymbol,
  formatCurrencyInput,
  handleCurrencyInputChange
} from '../utils/formatUtils';
import {
  fetchRequestById,
  setRequestTerms,
  confirmRequest,
  downloadRequestAttachment
} from './creditAssignmentApi';
import '../../styles/RequestDetail.css';

// Come parseCurrencyInput ma restituisce NaN (non 0) su input vuoto/invalido,
// così i controlli di validazione distinguono "campo non compilato".
const num = (v) => {
  if (v === '' || v === null || v === undefined) return NaN;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

// Valore numerico -> stringa formattata con separatore migliaia (o '' se assente).
const toInput = (v) => (typeof v === 'number' ? formatCurrencyInput(String(v)) : '');

const CreditAssignmentRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [terms, setTerms] = useState({
    advanceAmount: '',
    balanceAmount: '',
    managementFee: '',
    vehicleCost: ''
  });

  const load = useCallback(() => {
    setLoading(true);
    fetchRequestById(id)
      .then((r) => {
        setRequest(r);
        setTerms({
          advanceAmount: toInput(r.advanceAmount),
          balanceAmount: toInput(r.balanceAmount),
          managementFee: toInput(r.managementFee),
          vehicleCost: toInput(r.vehicleCost)
        });
      })
      .catch(() => setError(t('creditAssignment.errors.fetchRequest')))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const backPath = isAdmin
    ? '/admin/credit-assignment/requests'
    : '/company/credit-assignment/requests';

  const handleDownload = async () => {
    if (!request?.attachment) return;
    setDownloading(true);
    try {
      await downloadRequestAttachment(id, request.attachment);
    } catch {
      setError(t('creditAssignment.errors.download'));
    } finally {
      setDownloading(false);
    }
  };

  const advance = num(terms.advanceAmount);
  const balance = num(terms.balanceAmount);
  const mgmt = num(terms.managementFee);
  const vehicle = num(terms.vehicleCost);
  const previewTotal =
    Number.isFinite(mgmt) || Number.isFinite(vehicle)
      ? (Number.isFinite(mgmt) ? mgmt : 0) + (Number.isFinite(vehicle) ? vehicle : 0)
      : null;

  const saveTerms = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!Number.isFinite(advance) || advance < 0)
      return setError(t('creditAssignment.errors.invalidAdvance'));
    if (advance > request.invoiceAmount)
      return setError(t('creditAssignment.errors.advanceOverInvoice'));
    if (!Number.isFinite(balance) || balance < 0)
      return setError(t('creditAssignment.errors.invalidBalance'));
    if (balance > request.invoiceAmount)
      return setError(t('creditAssignment.errors.balanceOverInvoice'));
    if (!Number.isFinite(mgmt) || mgmt < 0)
      return setError(t('creditAssignment.errors.invalidManagementFee'));
    if (!Number.isFinite(vehicle) || vehicle < 0)
      return setError(t('creditAssignment.errors.invalidVehicleCost'));

    setSaving(true);
    try {
      const updated = await setRequestTerms(id, {
        advanceAmount: advance,
        balanceAmount: balance,
        managementFee: mgmt,
        vehicleCost: vehicle
      });
      setRequest(updated);
      setSuccess(t('creditAssignment.detail.termsSaved'));
    } catch (err) {
      setError(err.response?.data?.message || t('creditAssignment.errors.saveTerms'));
    } finally {
      setSaving(false);
    }
  };

  const doConfirm = async () => {
    if (!window.confirm(t('creditAssignment.detail.confirmPrompt'))) return;
    setError('');
    setSaving(true);
    try {
      const updated = await confirmRequest(id);
      setRequest(updated);
      setSuccess(t('creditAssignment.detail.confirmed'));
      setTimeout(() => navigate(backPath), 2500);
    } catch (err) {
      setError(err.response?.data?.message || t('creditAssignment.errors.confirm'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (!request) return <div className="error-message">{error || t('creditAssignment.errors.fetchRequest')}</div>;

  const currency = request.currency || 'EUR';
  const hasTerms = typeof request.advanceAmount === 'number';

  const infoCard = (label, value, modifier = '') => (
    <div className={`info-card ${modifier}`.trim()}>
      <div className="info-card-label">{label}</div>
      <div className="info-card-value">{value}</div>
    </div>
  );

  const row = (label, value) => (
    <div className="detail-item">
      <div className="detail-label">{label}:</div>
      <div className="detail-value">{value}</div>
    </div>
  );

  return (
    <div className="request-detail company-dashboard">
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button type="button" className="back-btn" onClick={() => navigate(backPath)}>
          ← {t('creditAssignment.detail.back')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <span>
            <Info className="card-title-icon" size={20} />
            {t('creditAssignment.detail.title')}
          </span>
          <div>
            <span className={`status status-${request.status}`}>
              {t(`creditAssignment.status.${request.status}`, { defaultValue: request.status })}
            </span>
          </div>
        </div>

        {error && <div className="alert alert-danger" style={{ marginTop: '15px' }}>{error}</div>}
        {success && <div className="alert-success" style={{ marginTop: '15px' }}><p>{success}</p></div>}

        <div className="request-info">
          <h3>{t('creditAssignment.detail.requestSection')}</h3>
          <div className="info-grid">
            {infoCard(t('creditAssignment.requests.id'), `#${request.requestId}`)}
            {isAdmin && request.company &&
              infoCard(t('creditAssignment.requests.company'), request.company.name)}
            {infoCard(t('creditAssignment.requests.client'), request.client?.clientName || '-')}
            {infoCard(
              t('creditAssignment.detail.invoiceAmount'),
              formatCurrencyWithSymbol(request.invoiceAmount, currency),
              'amount'
            )}
            {infoCard(
              t('creditAssignment.requests.date'),
              new Date(request.createdAt).toLocaleDateString()
            )}
            {infoCard(
              t('creditAssignment.detail.invoiceDocument'),
              request.attachment ? (
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleDownload}
                  disabled={downloading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <Download size={14} />
                  {downloading ? '…' : (request.attachment.originalName || t('common.download'))}
                </button>
              ) : (
                '-'
              )
            )}
          </div>
        </div>

        {/* --- ADMIN: form condizioni economiche --- */}
        {isAdmin && request.status !== 'completed' && (
          <form className="request-info" style={{ marginBottom: '25px' }} onSubmit={saveTerms}>
            <h3>{t('creditAssignment.detail.termsSection')}</h3>

            <div className="form-group">
              <label htmlFor="ca-advance">{t('creditAssignment.detail.advance')} <span className="req">*</span></label>
              <input
                id="ca-advance"
                type="text"
                inputMode="decimal"
                value={terms.advanceAmount}
                onChange={(e) => handleCurrencyInputChange(e, setTerms, terms, 'advanceAmount')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="ca-balance">{t('creditAssignment.detail.balance')} <span className="req">*</span></label>
              <input
                id="ca-balance"
                type="text"
                inputMode="decimal"
                value={terms.balanceAmount}
                onChange={(e) => handleCurrencyInputChange(e, setTerms, terms, 'balanceAmount')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="ca-mgmt">{t('creditAssignment.detail.managementFee')} <span className="req">*</span></label>
              <input
                id="ca-mgmt"
                type="text"
                inputMode="decimal"
                value={terms.managementFee}
                onChange={(e) => handleCurrencyInputChange(e, setTerms, terms, 'managementFee')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="ca-vehicle">{t('creditAssignment.detail.vehicleCost')} <span className="req">*</span></label>
              <input
                id="ca-vehicle"
                type="text"
                inputMode="decimal"
                value={terms.vehicleCost}
                onChange={(e) => handleCurrencyInputChange(e, setTerms, terms, 'vehicleCost')}
                required
              />
            </div>

            {row(
              t('creditAssignment.detail.totalCost'),
              previewTotal !== null ? formatCurrencyWithSymbol(previewTotal, currency) : '-'
            )}

            <div style={{ marginTop: '16px' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('common.loading') : t('creditAssignment.detail.saveTerms')}
              </button>
            </div>
          </form>
        )}

        {/* --- Riepilogo condizioni (azienda, o admin su richiesta completata) --- */}
        {(!isAdmin || request.status === 'completed') && (
          <div className="request-info">
            <h3>{t('creditAssignment.detail.termsSection')}</h3>
            {hasTerms ? (
              <div className="info-grid">
                {infoCard(t('creditAssignment.detail.advance'), formatCurrencyWithSymbol(request.advanceAmount, currency))}
                {infoCard(t('creditAssignment.detail.balance'), formatCurrencyWithSymbol(request.balanceAmount, currency))}
                {infoCard(t('creditAssignment.detail.managementFee'), formatCurrencyWithSymbol(request.managementFee, currency))}
                {infoCard(t('creditAssignment.detail.totalCost'), formatCurrencyWithSymbol(request.totalCost, currency), 'amount')}
              </div>
            ) : (
              <p style={{ color: '#6c757d' }}>{t('creditAssignment.detail.termsPending')}</p>
            )}
          </div>
        )}

        {/* --- AZIENDA: approvazione finale --- */}
        {!isAdmin && request.status === 'pending_approval' && (
          <div className="request-actions">
            <button type="button" className="btn btn-primary" onClick={doConfirm} disabled={saving}>
              {t('creditAssignment.detail.confirmOperation')}
            </button>
          </div>
        )}

        {request.status === 'completed' && (
          <div className="alert-success">
            <p>{t('creditAssignment.detail.completedMessage')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditAssignmentRequestDetail;
